import {
  getStoreCode,
  getCountryCode,
  getDefaultLanguage,
  getDefaultSizeSchema,
  setBrowseCurrency as setReduxBrowseCurrency,
  setKeyStoreDataversion as setReduxKeyStoreDataversion,
  getCurrencies,
  getStore as getRegionalStore,
  getRegionalStoreVariant,
  getStoreUrlForMen,
  getStoreUrlForWomen,
  getDefaultStoreUrl
} from "../../template/state/modules/regionalStore";
import {
  setBrowseCountry,
  setBrowseCurrency,
  setBrowseLanguage,
  setBrowseSizeSchema,
  setStoreCode,
  getGeoCountry,
  setTemplateVersions,
  setKeyStoreDataversion,
  getKeyStoreDataversion,
  setCurrency
} from "asos-site-chrome-store-presentation";
import {
  checkForMismatchAndSetCookie,
  correctUndefinedCookie
} from "./checkForMismatchAndSetCookie";
import getters from "./getters";
import {
  getAllSiteChromeVersions,
  getAllKeyStoreDataversions,
  getStoreCodeToSiteChromeVersionMappings,
  getSiteChromeVersionMappingsFromCookie
} from "./getAllVersions";
import {
  manifestWindowKey,
  asosGlobalNamespace,
  siteChromeGlobalNamespace,
  siteChromeTokenValuePlatform,
  siteChromeTokenValueGender,
  siteChromeTokenValues
} from "../../template/constants";
import { getCurrency } from "../../helpers/currency";
import { setKeyedCookieValue } from "./cookies";
import redirectToPreferredSite from "../redirectToPreferredSite";
import templateMatchesCountry from "./templateMatchesCountry";
import geoCountryMapsToCurrentStore from "./geoCountryMapsToCurrentStore";
import sanitiseFloor from "./sanitiseFloor";
import { getParamValue, isWindowDefined } from "../../helpers/url";
import { handleAffiliateId } from "./handleAffiliateId";
import { finishSettingCustomAttributeFromPreviousRequest } from "@src/helpers/monitoring";

const customAttributeNameForUndefinedKSDVCookie =
  "UndefinedKeyStoreDataversionCookieWasCorrected";

const getBrowseCurrency = currencies => {
  const { currency, currencyId } = getCurrency(
    parseInt(getParamValue("currencyId")),
    getParamValue("browseCurrency"),
    currencies
  );
  return {
    browseCurrency: currency,
    browseCurrencyId: currencyId
  };
};

const getSharedBaseContext = ({
  currencies,
  defaultLanguage,
  defaultSizeSchema,
  storeCode
}) => ({
  ...getBrowseCurrency(currencies),
  browseLanguage: defaultLanguage,
  browseSizeSchema: defaultSizeSchema,
  storeId: storeCode
});

const getBase = ({
  storeUrlForMen,
  storeUrlForWomen,
  defaultStoreUrl,
  browseCountry,
  regionalStoreVariant,
  platform,
  floor,
  ...regionalStoreFromState
}) => {
  const affiliateId = handleAffiliateId([
    storeUrlForMen,
    storeUrlForWomen,
    defaultStoreUrl
  ]);
  const geoCountry = getGeoCountry() || browseCountry;
  const base = {
    affiliateId,
    browseCountry: browseCountry && browseCountry.toUpperCase(),
    enableStoreBasedOperation: false,
    geoCountry,
    platform,
    floor,
    ...getSharedBaseContext(regionalStoreFromState)
  };

  if (regionalStoreVariant) {
    return {
      ...base,
      ...getSharedBaseContext(regionalStoreVariant)
    };
  }

  return base;
};

const stubContextForRedirectedPages = {
  pendingRedirect: true
};

const buildContextObject = (
  store,
  keyStoreDataversion,
  siteChromeVersions,
  latestManifest
) => {
  let contextObject = Object.assign(
    {},
    {
      keyStoreDataversion,
      templateVersion:
        siteChromeVersions.cookieValue || siteChromeVersions.templateValue
    }
  );

  const updateRegionalStore = callback => {
    // update the keystoredataversion in state
    store.dispatch(
      setReduxKeyStoreDataversion(contextObject.keyStoreDataversion)
    );
    // update browseCurrency in state
    store.dispatch(setReduxBrowseCurrency(contextObject.browseCurrency));

    if (callback) {
      callback();
    }
  };

  const state = store.getState();
  const storeCode = getStoreCode(state);

  const base = getBase({
    browseCountry: getCountryCode(state),
    storeCode,
    storeUrlForMen: getStoreUrlForMen(state),
    storeUrlForWomen: getStoreUrlForWomen(state),
    defaultStoreUrl: getDefaultStoreUrl(state),
    defaultLanguage: getDefaultLanguage(state),
    defaultSizeSchema: getDefaultSizeSchema(state),
    currencies: getCurrencies(state),
    regionalStoreVariant: getRegionalStoreVariant(
      getRegionalStore(state),
      contextObject.keyStoreDataversion
    ),
    platform:
      window[asosGlobalNamespace][siteChromeGlobalNamespace][
        siteChromeTokenValues
      ][siteChromeTokenValuePlatform],
    floor: sanitiseFloor(
      window[asosGlobalNamespace][siteChromeGlobalNamespace][
        siteChromeTokenValues
      ][siteChromeTokenValueGender]
    )
  });

  contextObject = {
    ...base,
    ...contextObject,
    buildVersion: state.buildVersion
  };

  if (!templateMatchesCountry(contextObject)) {
    if (geoCountryMapsToCurrentStore(getGeoCountry(), latestManifest)) {
      setBrowseCountry(getGeoCountry());
      window.location.reload();

      return stubContextForRedirectedPages;
    }
  }

  // Set new page cookies
  setCookies(contextObject);

  // Remove browse parameters from URL
  const url = ["browseCurrency", "browseCountry"].reduce(
    (current, value) => removeURLParameter(current, value),
    window.location.href
  );
  window.history.replaceState({}, "", url);

  // create context getters object, the external interface
  const contextGetters = getters(contextObject);

  return { context: contextGetters, updateRegionalStore };
};

const mustReload = (
  latestManifest,
  keyStoreDataversions,
  siteChromeVersions
) => {
  const storeCodeToSiteChromeVersionMappings =
    getStoreCodeToSiteChromeVersionMappings(latestManifest);

  const setTemplateVersionsCookie = setTemplateVersions.bind(
    null,
    storeCodeToSiteChromeVersionMappings
  );

  const setKeyStoreDataVersionCookie = setKeyStoreDataversion.bind(
    null,
    keyStoreDataversions.manifestValue
  );

  const siteChromeMismatch = checkForMismatchAndSetCookie(
    siteChromeVersions,
    setTemplateVersionsCookie
  );

  const ksdvMismatch =
    correctUndefinedCookie(
      keyStoreDataversions,
      setKeyStoreDataVersionCookie,
      customAttributeNameForUndefinedKSDVCookie
    ) ||
    checkForMismatchAndSetCookie(
      keyStoreDataversions,
      setKeyStoreDataVersionCookie
    );

  const mustReloadDueToMismatch = siteChromeMismatch || ksdvMismatch;

  return mustReloadDueToMismatch;
};

const updateCookieExpiry = latestManifest => {
  setTemplateVersions(getSiteChromeVersionMappingsFromCookie(latestManifest));
  setKeyStoreDataversion(getKeyStoreDataversion());
};

export function context(store) {
  const latestManifest = window[asosGlobalNamespace][manifestWindowKey];
  const state = store.getState();

  const keyStoreDataversions = getAllKeyStoreDataversions(
    state,
    latestManifest
  );

  const keyStoreDataversion =
    keyStoreDataversions.cookieValue || keyStoreDataversions.manifestValue;

  if (redirectToPreferredSite(latestManifest, state, keyStoreDataversion)) {
    return stubContextForRedirectedPages;
  }

  const siteChromeVersions = getAllSiteChromeVersions(state, latestManifest);

  const shouldReload = mustReload(
    latestManifest,
    keyStoreDataversions,
    siteChromeVersions
  );

  updateCookieExpiry(latestManifest);

  if (shouldReload) {
    window.location.reload();
  } else {
    finishSettingCustomAttributeFromPreviousRequest(
      customAttributeNameForUndefinedKSDVCookie
    );
  }

  return buildContextObject(
    store,
    keyStoreDataversion,
    siteChromeVersions,
    latestManifest
  );
}

function removeURLParameter(url, parameter) {
  const urlParts = url.split("?");
  if (urlParts.length >= 2) {
    const prefix = encodeURIComponent(parameter) + "=";
    const parts = urlParts[1].split(/[&;]/g);

    for (let i = parts.length; i-- > 0; ) {
      if (parts[i].lastIndexOf(prefix, 0) !== -1) {
        parts.splice(i, 1);
      }
    }

    return urlParts[0] + (parts.length > 0 ? "?" + parts.join("&") : "");
  } else {
    return url;
  }
}

function stripPrefSiteFromAsosCookie() {
  setKeyedCookieValue("asos", "PreferredSite", "");
}

/*
 * @param {Object} webContext
 * @param {string} webContext.browseCountry
 * @param {string} webContext.browseCurrency
 * @param {string} webContext.browseLanguage
 * @param {string} webContext.browseSizeSchema
 * @param {string} webContext.storeId
 * @param {string} webContext.browseCurrencyId
 */
export function setCookies(webContext) {
  // true represents session cookie
  //initAsosCookie();
  stripPrefSiteFromAsosCookie();
  setBrowseCountry(webContext.browseCountry);
  setBrowseCurrency(webContext.browseCurrency);
  setBrowseLanguage(webContext.browseLanguage);
  setBrowseSizeSchema(webContext.browseSizeSchema);
  setStoreCode(webContext.storeId);
  setCurrency({
    currencyCode: webContext.browseCurrency,
    currencyId: webContext.browseCurrencyId
  });
}

export function getContext() {
  return (
    isWindowDefined() &&
    window[asosGlobalNamespace] !== undefined &&
    window[asosGlobalNamespace].webContext
  );
}
